import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';

const swatches = [
  { name: 'Primary', value: 'var(--primary)', className: 'bg-primary text-primary-foreground' },
  { name: 'Primary Hover', value: 'var(--primary-hover)', className: 'bg-primary-hover text-primary-foreground' },
  { name: 'Primary Tint', value: 'var(--primary-tint)', className: 'bg-primary-tint text-foreground' },
  { name: 'Gold', value: 'var(--accent-gold)', className: 'bg-gold text-gold-foreground' },
  { name: 'Sky', value: 'var(--accent-sky)', className: 'bg-sky text-sky-foreground' },
  { name: 'Success', value: 'var(--success)', className: 'bg-success text-success-foreground' },
  { name: 'Warning', value: 'var(--warning)', className: 'bg-warning text-warning-foreground' },
  { name: 'Danger', value: 'var(--danger)', className: 'bg-danger text-danger-foreground' },
  { name: 'Info', value: 'var(--info)', className: 'bg-info text-info-foreground' },
];

const badges = [
  { name: 'Success', icon: Check, className: 'bg-success-tint text-success-foreground border border-success/30' },
  { name: 'Warning', className: 'bg-warning-tint text-warning border border-warning/30' },
  { name: 'Danger', className: 'bg-danger-tint text-danger border border-danger/30' },
  { name: 'Info', className: 'bg-info-tint text-info border border-info/30' },
];

const chartBars = [
  { value: '76%', className: 'bg-chart-1' },
  { value: '48%', className: 'bg-chart-2' },
  { value: '68%', className: 'bg-chart-3' },
  { value: '35%', className: 'bg-chart-4' },
  { value: '58%', className: 'bg-chart-5' },
  { value: '42%', className: 'bg-chart-6' },
];

export default function DesignCheck() {
  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Theme review</p>
          <h1 className="mt-2 text-3xl font-bold text-foreground">Royal Indigo & Gold</h1>
          <span className="mt-1 inline-block text-xs font-semibold text-muted-foreground" data-testid="theme-version">theme v2</span>
        </div>
        <Button className="bg-primary text-primary-foreground">Primary CTA</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {swatches.map((swatch) => (
          <Card key={swatch.name} className="overflow-hidden border border-border bg-surface">
            <div className={`h-20 w-full ${swatch.className}`} />
            <div className="p-3">
              <div className="font-medium text-foreground">{swatch.name}</div>
              <div className="text-xs text-muted-foreground">{swatch.value}</div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Badges</h2>
          <div className="flex flex-wrap gap-2">
            {badges.map((badge) => (
              <span key={badge.name} className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${badge.className}`}>
                <span className="mr-2 h-2 w-2 rounded-full bg-current opacity-80" />
                {badge.icon && <badge.icon size={12} className="mr-1" />}
                {badge.name}
              </span>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="mb-4 text-lg font-semibold text-foreground">Buttons</h2>
          <div className="flex flex-wrap gap-3">
            <Button className="bg-primary text-primary-foreground">Primary</Button>
            <Button variant="outline" className="border border-border bg-surface text-foreground">Secondary</Button>
            <Button className="bg-warning text-warning-foreground">Warning</Button>
            <Button className="bg-danger text-danger-foreground">Danger</Button>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <h2 className="mb-4 text-lg font-semibold text-foreground">Form / Table preview</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <div className="rounded-xl border border-border bg-surface p-3">
              <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">Field</label>
              <input value="Sample entry" className="w-full rounded-xl border border-border bg-surface-elevated px-3 py-2 text-sm text-foreground outline-none" readOnly />
            </div>
            <div className="rounded-xl border border-border bg-surface p-3">
              <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">Phone</label>
              <input value="+91 99999 99999" className="w-full rounded-xl border border-border bg-surface-elevated px-3 py-2 text-sm text-foreground outline-none" readOnly />
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-elevated text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Service</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border">
                  <td className="px-3 py-2">Deep Clean</td>
                  <td className="px-3 py-2"><span className="inline-flex items-center gap-1 rounded-full bg-success-tint px-2 py-1 text-xs text-success-foreground"><Check size={12} />Done</span></td>
                  <td className="px-3 py-2">₹10,000</td>
                </tr>
                <tr className="border-t border-border">
                  <td className="px-3 py-2">Nano-Coating</td>
                  <td className="px-3 py-2"><span className="rounded-full bg-warning-tint px-2 py-1 text-xs text-warning">Review</span></td>
                  <td className="px-3 py-2">₹18,500</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      <Card className="p-5" data-testid="theme-chart-preview">
        <h2 className="mb-4 text-lg font-semibold text-foreground">Chart palette</h2>
        <div className="flex h-40 items-end gap-3 border-b border-border pb-2" role="img" aria-label="Six theme chart colors in a flat bar chart">
          {chartBars.map((bar, index) => (
            <div key={bar.className} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
              <div className={`w-full rounded-t-sm ${bar.className}`} style={{ height: bar.value }} />
              <span className="text-xs text-muted-foreground">{index + 1}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
